import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Brain, ChevronDown } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";

const DEMO_URL = "https://psytech-platform.on-forge.com/en/login";

const SOLUTIONS = [
  { to: "/for-clinics", label: "For Clinics", desc: "Intake, assessment, and review" },
  { to: "/for-education", label: "For Education", desc: "Student wellbeing support" },
  { to: "/for-companies", label: "For Companies", desc: "Workplace wellbeing programs" },
  { to: "/for-individuals", label: "For Individuals", desc: "Guided self-reflection" },
];

const LINKS = [
  { to: "/pilot-program", label: "Pilot Program" },
  { to: "/trust-safety", label: "Trust & Safety" },
  { to: "/privacy", label: "Privacy" },
  { to: "/insights", label: "Insights" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutions, setMobileSolutions] = useState(false);
  const solutionsRef = useRef(null);
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target)) setSolutionsOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const scrollToHash = (hash) => {
    if (!isHomePage) {
      window.location.href = "/" + hash;
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  const dark = !isHomePage || isScrolled;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        dark ? "bg-white/90 backdrop-blur-lg shadow-sm border-b border-slate-100" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo" className="flex items-center gap-2 group flex-shrink-0">
            <img
              src="/brand/psytech-logo-128.png"
              srcSet="/brand/psytech-logo-128.png 1x, /brand/psytech-logo-256.png 2x"
              alt="PsyTech"
              width="36"
              height="40"
              className="h-9 w-auto md:h-10 select-none"
              draggable={false}
            />
            <span className={`text-lg font-bold font-['Plus_Jakarta_Sans'] transition-colors ${dark ? "text-slate-900" : "text-white"}`}>
              PsyTech
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {/* Solutions dropdown */}
            <div className="relative" ref={solutionsRef}>
              <button
                data-testid="nav-solutions-trigger"
                onMouseEnter={() => setSolutionsOpen(true)}
                onClick={() => setSolutionsOpen((s) => !s)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  dark ? "text-slate-600 hover:text-cyan-700" : "text-slate-300 hover:text-white"
                }`}
              >
                Solutions <ChevronDown className={`w-3.5 h-3.5 transition-transform ${solutionsOpen ? "rotate-180" : ""}`} />
              </button>
              {solutionsOpen && (
                <div
                  onMouseLeave={() => setSolutionsOpen(false)}
                  data-testid="nav-solutions-menu"
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-slate-200 p-2 animate-fade-in"
                >
                  {SOLUTIONS.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setSolutionsOpen(false)}
                      data-testid={`nav-solution-${s.to.replace("/", "")}`}
                      className="block px-3 py-2.5 rounded-lg hover:bg-cyan-50 group"
                    >
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-cyan-700">{s.label}</div>
                      <div className="text-xs text-slate-500">{s.desc}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`text-sm font-medium transition-colors ${
                  dark ? "text-slate-600 hover:text-cyan-700" : "text-slate-300 hover:text-white"
                } ${location.pathname === l.to ? (dark ? "text-cyan-700" : "text-white") : ""}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right CTAs */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <LanguageSelector variant={dark ? "light" : "dark"} />
            <Button
              variant="ghost"
              onClick={() => scrollToHash("#contact")}
              data-testid="nav-contact-btn"
              className={`rounded-full transition-colors text-sm ${
                dark ? "text-slate-600 hover:text-cyan-700 hover:bg-cyan-50" : "text-slate-300 hover:text-white hover:bg-white/10"
              }`}
            >
              {t('nav.contact')}
            </Button>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.__psyTrack && window.__psyTrack("button_click", { button: "open-app", location: "navbar-desktop" })}
              data-testid="nav-demo-btn"
              className="inline-flex items-center justify-center rounded-full bg-cyan-700 text-white hover:bg-cyan-800 shadow-lg shadow-cyan-900/20 transition-all hover:scale-105 px-4 py-2 text-sm font-medium"
            >
              {t('nav.requestDemo')}
            </a>
          </div>

          {/* Mobile toggle */}
          <div className="flex items-center gap-2 lg:hidden">
            <LanguageSelector variant={dark ? "light" : "dark"} />
            <button
              data-testid="mobile-menu-toggle"
              className="p-2"
              aria-label="Toggle menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? (
                <X className={`w-6 h-6 ${dark ? "text-slate-900" : "text-white"}`} />
              ) : (
                <Menu className={`w-6 h-6 ${dark ? "text-slate-900" : "text-white"}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-slate-100 shadow-lg animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-4 py-4 space-y-1">
              <button
                onClick={() => setMobileSolutions((v) => !v)}
                className="w-full flex items-center justify-between py-3 text-slate-700 font-semibold"
                data-testid="mobile-solutions-trigger"
              >
                Solutions
                <ChevronDown className={`w-4 h-4 transition-transform ${mobileSolutions ? "rotate-180" : ""}`} />
              </button>
              {mobileSolutions && (
                <div className="pl-4 space-y-1 pb-2 border-l-2 border-cyan-100">
                  {SOLUTIONS.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-sm text-slate-600 hover:text-cyan-700"
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileOpen(false)}
                  className="block py-3 text-slate-700 font-medium hover:text-cyan-700"
                >
                  {l.label}
                </Link>
              ))}
              <button
                onClick={() => scrollToHash("#contact")}
                className="block w-full text-left py-3 text-slate-700 font-medium hover:text-cyan-700"
              >
                {t('nav.contact')}
              </button>
              <div className="pt-4 border-t border-slate-100">
                <a
                  href={DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => window.__psyTrack && window.__psyTrack("button_click", { button: "open-app", location: "navbar-mobile" })}
                  className="flex items-center justify-center w-full rounded-full bg-cyan-700 text-white hover:bg-cyan-800 px-4 py-2.5 text-sm font-medium transition-colors"
                >
                  {t('nav.requestDemo')}
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
